import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import axios from '../axios';
import { ButtonWithText } from '../components/ButtonWithText';
import RemoveTSPModal from '../components/RemoveTSPModal';
import SkipAdditionalTSP from '../components/SkipAdditionalTSP';
import { useAppState } from '../contexts/AppStateProvider';
import { useJWT } from '../contexts/JWTProvider';
import { ServiceThemeOptions } from '../themes';
import { getTSPImageNameByKey, getTSPNameByKey } from '../tsps';
import { createHoverBackground } from '../utils/ThemeUtils';
import { Analytics, createAnalyticsContext } from '../utils/analytics';

export default function UpdatePermissions() {
  const theme = useTheme() as ServiceThemeOptions;
  const navigate = useNavigate();
  const [skipDialogOpen, setSkipDialogOpen] = useState(false);
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [tspToRemove, setTspToRemove] = useState<string>('');
  const [isRemoving, setIsRemoving] = useState(false);
  const { search } = useLocation();

  const jwt = useJWT();
  const {
    TSP,
    setTSP,
    existingTSPs,
    setExistingTSPs,
    setApproved,
    setUpdatingTSP,
    setTspCount,
    getTSPIDByKey,
  } = useAppState();

  // Extract theme values safely with proper type casting
  const primaryColor = (theme.palette?.primary as any)?.main || '#B60207';
  const hoverBackground = createHoverBackground(primaryColor, 0.08);
  const paperBackground =
    (theme.palette?.background as any)?.paper || '#FFFFFF';

  const onCancel = () => {
    setSkipDialogOpen(true);
  };

  const handleSkipConfirm = () => {
    // Track that user chose not to add/update additional TSPs
    const analyticsContext = createAnalyticsContext(jwt);
    Analytics.pageView(analyticsContext, 'SkipAdditionalTSPs');

    // Check if both redirect URIs are null (new window scenario)
    const hasSuccessRedirect =
      jwt.redirectURISuccess && jwt.redirectURISuccess !== 'null';
    const hasErrorRedirect =
      jwt.redirectURIError && jwt.redirectURIError !== 'null';

    if (!hasSuccessRedirect && !hasErrorRedirect) {
      // New window scenario - try to close the window
      try {
        // Try to close the window first
        window.close();

        // If window.close() didn't work (popup blockers, etc.),
        // show a message after a brief delay
        setTimeout(() => {
          alert(
            'Your existing integrations remain active. You can safely close this window.'
          );
        }, 100);
      } catch (error) {
        // Fallback: show close message
        alert(
          'Your existing integrations remain active. You can safely close this window.'
        );
      }
      return;
    }

    // Use JWT success redirect, then error redirect as fallback
    const redirectURL = jwt.redirectURISuccess || jwt.redirectURIError || '/';

    if (redirectURL.startsWith('http')) {
      // External redirect - import and use safeRedirect
      import('../utils/urlUtils').then(({ safeRedirect }) => {
        safeRedirect(redirectURL);
      });
    } else {
      // Internal redirect
      navigate(redirectURL);
    }
  };

  const handleSkipDialogClose = () => {
    setSkipDialogOpen(false);
  };

  const onAddNewTSP = () => {
    // Track that user chose to add a new TSP (Analytics handles both Amplitude and Slack)
    const analyticsContext = createAnalyticsContext(jwt);
    Analytics.pageView(analyticsContext, 'AddNewTSP');

    setApproved(true);
    if (TSP) {
      navigate({
        pathname: `/onboard`,
        search: search,
      });
    } else {
      navigate({
        pathname: '/selectTSP',
        search: search, // No 'from' parameter - this is a new TSP selection flow
      });
    }
  };

  const handleRemoveTSP = (tspKey: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent the parent click handler from firing
    setTspToRemove(tspKey);
    setRemoveModalOpen(true);
  };

  const handleRemoveModalClose = () => {
    setRemoveModalOpen(false);
    setTspToRemove('');
  };

  const handleConfirmRemove = async () => {
    if (!tspToRemove) return;

    setIsRemoving(true);

    // Get the actual TSP ID for the API call
    const tspId = getTSPIDByKey(tspToRemove);

    if (!tspId) {
      console.error('[RemoveTSP] Could not find TSP ID for key:', tspToRemove);
      alert('Could not find TSP ID. Please refresh the page and try again.');
      setIsRemoving(false);
      return;
    }

    try {
      console.log('[RemoveTSP] Attempting to remove TSP:', {
        tspKey: tspToRemove,
        tspId,
        tspName: getTSPNameByKey(tspToRemove),
        existingTSPsCount: existingTSPs.length,
        jwt: {
          isValid: jwt.isValid,
          fleetCode: jwt.fleetCode,
          hasToken: !!jwt.rawToken,
          serviceId: jwt.serviceId,
        },
        url: `/service/remove_tsp?axle_tsp_id=${tspId}`,
      });

      // Use the actual TSP ID from the mapping
      const response = await axios.delete(
        `/service/remove_tsp?axle_tsp_id=${tspId}`
      );

      if (response.status === 200) {
        console.log('[RemoveTSP] Successfully removed TSP:', response.data);

        // Update the existing TSPs list by removing the deleted TSP
        const updatedTSPs = existingTSPs.filter((tsp) => tsp !== tspToRemove);

        // Track successful removal
        const analyticsContext = createAnalyticsContext(jwt);
        Analytics.tspRemoved(
          analyticsContext,
          tspToRemove,
          getTSPNameByKey(tspToRemove) || tspToRemove
        );

        // Close modal
        setRemoveModalOpen(false);
        setTspToRemove('');

        // If no TSPs left, redirect immediately to avoid API errors with empty fleet
        if (updatedTSPs.length === 0) {
          console.log(
            '[RemoveTSP] No TSPs remaining, redirecting to TSP selection'
          );
          navigate('/selectTSP', { replace: true });
          return; // Exit early to avoid updating state that might cause issues
        }

        // Only update state if there are still TSPs remaining
        setExistingTSPs(updatedTSPs);
        setTspCount(updatedTSPs.length);
      }
    } catch (error: any) {
      console.error('[RemoveTSP] Failed to remove TSP:', {
        error,
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });

      // Get the actual error message from the response
      const errorMessage =
        error.response?.data || error.message || 'Unknown error';

      // Handle specific error cases
      if (error.response?.status === 400) {
        if (
          typeof errorMessage === 'string' &&
          errorMessage.includes('cannot remove the last TSP')
        ) {
          alert(
            'Cannot remove the last TSP connection. You must have at least one TSP connection.'
          );
        } else if (
          typeof errorMessage === 'string' &&
          errorMessage.includes('does not belong to fleet')
        ) {
          alert('This TSP connection does not belong to your fleet.');
        } else {
          alert(`Cannot remove TSP: ${errorMessage}`);
        }
      } else if (error.response?.status === 403) {
        alert('You do not have permission to remove this TSP connection.');
      } else if (error.response?.status === 404) {
        alert('TSP connection not found or fleet not found.');
      } else {
        alert(`Failed to remove TSP connection: ${errorMessage}`);
      }
    } finally {
      setIsRemoving(false);
    }
  };

  useEffect(() => {
    if (!jwt.isValid) {
      // Let JWT handlers determine appropriate error message and actions
      navigate('/error');
    } else {
      // Track page view (Analytics handles both Amplitude and Slack)
      const analyticsContext = createAnalyticsContext(jwt);
      Analytics.pageView(analyticsContext, 'UpdateOrAdd');
    }

    // If we somehow ended up here with no TSPs, redirect to TSP selection
    if (existingTSPs.length === 0) {
      console.log(
        '[UpdatePermissions] No existing TSPs found, redirecting to TSP selection'
      );
      navigate('/selectTSP', { replace: true });
      return;
    }

    // Debug: Log TSP information
    console.log('[UpdatePermissions] TSP Debug Info:', {
      existingTSPs,
      tspNames: existingTSPs.map((key) => ({
        key,
        name: getTSPNameByKey(key),
        fallback: getTSPNameByKey(key) || key,
      })),
    });
  }, [jwt.isValid, navigate, jwt, existingTSPs]);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        minHeight: '100vh',
        width: '100%',
        background: 'transparent',
        py: { xs: 4, sm: 8 },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: { xs: 'calc(100% - 24px)', sm: '500px' },
          maxWidth: { xs: 'calc(100vw - 24px)', sm: '500px' },
          minHeight: { xs: 'auto', sm: '600px' },
          backgroundColor:
            (theme.palette?.background as any)?.paper || '#FFFFFF',
          borderRadius: '4px',
          overflow: 'hidden',
          mb: { xs: 4, sm: 2 },
          mx: { xs: 1, sm: 'auto' },
        }}
      >
        <Box
          sx={{
            padding: { xs: '40px 16px 24px', sm: '60px 50px 24px' },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            minWidth: { xs: 'calc(100vw - 48px)', sm: 'auto' },
          }}
        >
          <Typography
            variant='h5'
            align='center'
            sx={{
              mb: 4,
              fontWeight: 800,
              fontSize: { xs: '22px', sm: '28px' },
              lineHeight: { xs: '28px', sm: '36px' },
              maxWidth: '449px',
              wordWrap: 'break-word',
              hyphens: 'auto',
            }}
          >
            You have already connected {existingTSPs.length}{' '}
            {existingTSPs.length > 1 ? 'devices' : 'device'}.
          </Typography>

          <Box sx={{ width: '100%', mb: 4, maxWidth: '449px' }}>
            <Divider>
              <Chip
                label='UPDATE'
                color='primary'
                sx={{
                  fontWeight: 500,
                  fontSize: { xs: '12px', sm: '14px' },
                  '&.MuiChip-colorPrimary': {
                    backgroundColor:
                      (theme.custom?.buttonTextColor as string) || '#FFFFFF',
                    color: primaryColor,
                  },
                }}
              />
            </Divider>
          </Box>

          <Grid container spacing={2} sx={{ mb: 4, maxWidth: '449px' }}>
            {existingTSPs.map((key: string, index: number) => (
              <Grid
                item
                key={`tsp-${key}-${index}`}
                xs={12}
                sm={existingTSPs.length > 1 ? 6 : 12}
                sx={{
                  // When there's only 1 TSP, limit the maximum width to keep consistent box sizing
                  ...(existingTSPs.length === 1 && {
                    display: 'flex',
                    justifyContent: 'center',
                    '& > *': {
                      maxWidth: 'calc(50% - 8px)', // Same as when there are 2 TSPs (accounting for spacing)
                      width: '100%',
                    },
                  }),
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'background-color 0.3s',
                    backgroundColor: alpha(paperBackground, 0.6),
                    '&:hover': {
                      backgroundColor:
                        (theme.palette?.action as any)?.hover ||
                        hoverBackground,
                    },
                    padding: { xs: 1.5, sm: 2 },
                    borderRadius: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    height: '100%',
                  }}
                  onClick={() => {
                    // Track TSP selection for update (Analytics handles both Amplitude and Slack)
                    const analyticsContext = createAnalyticsContext(jwt);
                    Analytics.tspSelected(
                      analyticsContext,
                      key,
                      getTSPNameByKey(key) || key
                    );

                    setTSP(key);
                    setApproved(true);
                    setUpdatingTSP(true);
                    navigate({
                      pathname: `/config/${key}`,
                      search:
                        search + (search ? '&' : '?') + 'from=updateOrAdd',
                    });
                  }}
                >
                  {/* Remove button - positioned in top left corner */}
                  <IconButton
                    onClick={(event) => handleRemoveTSP(key, event)}
                    sx={{
                      position: 'absolute',
                      top: 4,
                      left: 4,
                      width: '24px',
                      height: '24px',
                      backgroundColor: createHoverBackground(
                        primaryColor,
                        0.12
                      ),
                      color: primaryColor,
                      zIndex: 1,
                      '&:hover': {
                        backgroundColor: primaryColor,
                        color: '#FFFFFF',
                      },
                    }}
                    size='small'
                  >
                    <CloseIcon sx={{ fontSize: '16px' }} />
                  </IconButton>

                  <Box sx={{ mb: 2 }}>
                    <Box
                      component='img'
                      src={`/images/${getTSPImageNameByKey(key)}.png?w=248&fit=crop&auto=format`}
                      alt={getTSPNameByKey(key)}
                      loading='lazy'
                      sx={{
                        maxHeight: { xs: '60px', sm: '75px' },
                        maxWidth: { xs: '80px', sm: '100px' },
                        objectFit: 'contain',
                      }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: { xs: '14px', sm: '16px' },
                      lineHeight: { xs: '20px', sm: '24px' },
                      textAlign: 'center',
                    }}
                  >
                    {getTSPNameByKey(key)}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ width: '100%', mb: 4, maxWidth: '449px' }}>
            <Divider>
              <Chip
                label='OR'
                color='primary'
                sx={{
                  fontWeight: 500,
                  fontSize: { xs: '12px', sm: '14px' },
                  '&.MuiChip-colorPrimary': {
                    backgroundColor:
                      (theme.custom?.buttonTextColor as string) || '#FFFFFF',
                    color: primaryColor,
                  },
                }}
              />
            </Divider>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              gap: '10px',
              maxWidth: '449px',
            }}
          >
            <ButtonWithText
              onClick={onAddNewTSP}
              sx={{
                height: { xs: '48px', sm: '40px' },
                width: '100%',
                maxWidth: { xs: '100%', sm: '392px' },
                fontSize: { xs: '16px', sm: '14px' },
              }}
            >
              ADD NEW TSP
            </ButtonWithText>

            <Button
              variant='text'
              color='error'
              onClick={onCancel}
              sx={{
                height: { xs: '48px', sm: '40px' },
                width: '100%',
                maxWidth: { xs: '100%', sm: '392px' },
                color: primaryColor,
                textTransform: 'uppercase',
                fontWeight: 700,
                fontSize: { xs: '16px', sm: '14px' },
                lineHeight: '16px',
                letterSpacing: '0.75px',
                backgroundColor: 'transparent',
                padding: '12px',
                '&:hover': {
                  backgroundColor: hoverBackground,
                },
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Skip Additional TSP Dialog */}
      <SkipAdditionalTSP
        open={skipDialogOpen}
        onClose={handleSkipDialogClose}
        onSkip={handleSkipConfirm}
        existingTSPsCount={existingTSPs.length}
      />

      {/* Remove TSP Modal */}
      <RemoveTSPModal
        open={removeModalOpen}
        onClose={handleRemoveModalClose}
        onConfirmRemove={handleConfirmRemove}
        tspName={getTSPNameByKey(tspToRemove) || tspToRemove}
        isLoading={isRemoving}
      />
    </Box>
  );
}
